import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Activity } from 'lucide-react';

interface FinancialHealthMeterProps {
    completedAmount: number;
    pendingAmount: number;
    processingAmount?: number;
    title?: string;
    description?: string;
    className?: string;
}

const formatCurrency = (value: number): string => {
    if (value >= 10000000) {
        return `₹${(value / 10000000).toFixed(2)}Cr`;
    } else if (value >= 100000) {
        return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
        return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value.toLocaleString('en-IN')}`;
};

function getHealthScore(completed: number, pending: number): number {
    const total = completed + pending;
    if (total === 0) return 100;
    return Math.round((completed / total) * 100);
}

function getHealthColor(score: number): { primary: string; secondary: string; bg: string; text: string } {
    if (score >= 80) {
        return {
            primary: '#10b981', // emerald-500
            secondary: '#34d399', // emerald-400
            bg: 'from-emerald-500 to-green-500',
            text: 'text-emerald-600 dark:text-emerald-400',
        };
    } else if (score >= 60) {
        return {
            primary: '#22c55e', // green-500
            secondary: '#4ade80', // green-400
            bg: 'from-green-500 to-lime-500',
            text: 'text-green-600 dark:text-green-400',
        };
    } else if (score >= 40) {
        return {
            primary: '#f59e0b', // amber-500
            secondary: '#fbbf24', // amber-400
            bg: 'from-amber-500 to-yellow-500',
            text: 'text-amber-600 dark:text-amber-400',
        };
    } else if (score >= 20) {
        return {
            primary: '#f97316', // orange-500
            secondary: '#fb923c', // orange-400
            bg: 'from-orange-500 to-amber-500',
            text: 'text-orange-600 dark:text-orange-400',
        };
    } else {
        return {
            primary: '#ef4444', // red-500
            secondary: '#f87171', // red-400
            bg: 'from-red-500 to-orange-500',
            text: 'text-red-600 dark:text-red-400',
        };
    }
}

function getHealthLabel(score: number): string {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    if (score >= 20) return 'Needs Attention';
    return 'Critical';
}

// SVG-based semi-circular gauge
function GaugeMeter({ score, colors }: { score: number; colors: ReturnType<typeof getHealthColor> }) {
    const radius = 80;
    const strokeWidth = 12;
    const center = 100;

    // Semi-circle: from 180° to 0° (left to right arc)
    const startAngle = 180;
    const endAngle = 0;
    const angleRange = startAngle - endAngle;

    // Calculate the arc for the progress
    const progressAngle = startAngle - (score / 100) * angleRange;

    // Convert angle to radians
    const toRadians = (angle: number) => (angle * Math.PI) / 180;

    // Calculate arc path
    const getArcPath = (startA: number, endA: number, r: number) => {
        const start = {
            x: center + r * Math.cos(toRadians(startA)),
            y: center - r * Math.sin(toRadians(startA)),
        };
        const end = {
            x: center + r * Math.cos(toRadians(endA)),
            y: center - r * Math.sin(toRadians(endA)),
        };
        const largeArcFlag = Math.abs(startA - endA) > 180 ? 1 : 0;

        return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
    };

    // Needle position
    const needleAngle = progressAngle;
    const needleLength = radius - 20;
    const needleEnd = {
        x: center + needleLength * Math.cos(toRadians(needleAngle)),
        y: center - needleLength * Math.sin(toRadians(needleAngle)),
    };

    return (
        <svg viewBox="0 0 200 120" className="w-full max-w-[280px] mx-auto">
            <defs>
                {/* Background gradient */}
                <linearGradient id="gauge-bg-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity="0.2" />
                    <stop offset="25%" stopColor="#f97316" stopOpacity="0.2" />
                    <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.2" />
                    <stop offset="75%" stopColor="#22c55e" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity="0.2" />
                </linearGradient>

                {/* Progress gradient */}
                <linearGradient id="gauge-progress-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={colors.primary} />
                    <stop offset="100%" stopColor={colors.secondary} />
                </linearGradient>

                {/* Glow filter */}
                <filter id="glow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                    <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            {/* Background arc */}
            <path
                d={getArcPath(180, 0, radius)}
                fill="none"
                stroke="url(#gauge-bg-gradient)"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
            />

            {/* Progress arc */}
            <path
                d={getArcPath(180, Math.max(progressAngle, 0), radius)}
                fill="none"
                stroke="url(#gauge-progress-gradient)"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                filter="url(#glow)"
                className="transition-all duration-1000"
            />

            {/* Tick marks */}
            {[0, 25, 50, 75, 100].map((tick) => {
                const tickAngle = 180 - (tick / 100) * 180;
                const innerR = radius + 8;
                const outerR = radius + 14;
                const x1 = center + innerR * Math.cos(toRadians(tickAngle));
                const y1 = center - innerR * Math.sin(toRadians(tickAngle));
                const x2 = center + outerR * Math.cos(toRadians(tickAngle));
                const y2 = center - outerR * Math.sin(toRadians(tickAngle));
                return (
                    <line
                        key={tick}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="text-muted-foreground/50"
                    />
                );
            })}

            {/* Needle */}
            <g className="transition-all duration-1000" style={{ transformOrigin: `${center}px ${center}px` }}>
                {/* Needle shadow */}
                <line
                    x1={center}
                    y1={center}
                    x2={needleEnd.x + 1}
                    y2={needleEnd.y + 1}
                    stroke="rgba(0,0,0,0.2)"
                    strokeWidth="3"
                    strokeLinecap="round"
                />
                {/* Needle */}
                <line
                    x1={center}
                    y1={center}
                    x2={needleEnd.x}
                    y2={needleEnd.y}
                    stroke={colors.primary}
                    strokeWidth="3"
                    strokeLinecap="round"
                />
                {/* Needle center */}
                <circle
                    cx={center}
                    cy={center}
                    r="6"
                    fill={colors.primary}
                    className="drop-shadow-md"
                />
                <circle
                    cx={center}
                    cy={center}
                    r="3"
                    fill="white"
                />
            </g>
        </svg>
    );
}

export function FinancialHealthMeter({
    completedAmount,
    pendingAmount,
    processingAmount = 0,
    title = 'Payout Health',
    description = 'Completed vs pending payouts ratio',
    className = '',
}: FinancialHealthMeterProps) {
    const healthScore = getHealthScore(completedAmount, pendingAmount + processingAmount);
    const colors = getHealthColor(healthScore);
    const healthLabel = getHealthLabel(healthScore);
    const totalAmount = completedAmount + pendingAmount + processingAmount;

    return (
        <Card className={`overflow-hidden ${className}`}>
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                    <Activity className="h-5 w-5 text-primary" />
                    {title}
                </CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
                {/* Gauge Meter */}
                <div className="relative">
                    <GaugeMeter score={healthScore} colors={colors} />

                    {/* Center score display */}
                    <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
                        <span className={`text-4xl font-bold ${colors.text}`}>
                            {healthScore}%
                        </span>
                        <span className={`text-sm font-medium ${colors.text}`}>
                            {healthLabel}
                        </span>
                    </div>
                </div>

                {/* Legend */}
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                        <p className="text-xs text-muted-foreground">Completed</p>
                        <p className="font-semibold text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(completedAmount)}
                        </p>
                    </div>
                    <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                        <p className="text-xs text-muted-foreground">Pending</p>
                        <p className="font-semibold text-amber-600 dark:text-amber-400">
                            {formatCurrency(pendingAmount)}
                        </p>
                    </div>
                    <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                        <p className="text-xs text-muted-foreground">Processing</p>
                        <p className="font-semibold text-blue-600 dark:text-blue-400">
                            {formatCurrency(processingAmount)}
                        </p>
                    </div>
                </div>

                {/* Total */}
                <div className="mt-3 pt-3 border-t text-center">
                    <p className="text-xs text-muted-foreground">Total Payouts</p>
                    <p className="text-lg font-bold">{formatCurrency(totalAmount)}</p>
                </div>
            </CardContent>
        </Card>
    );
}

export default FinancialHealthMeter;
